import {
  useEffect,
  useState,
} from 'react'

import {
  calcApi,
} from 'api/calc'
import {
  useInvoicesByOrderIdQuery,
} from 'api/calc'
import {
  useAppDispatch,
} from 'store'

export function normalizeTnvedCode(code: string | null | undefined): string {
  if (code == null) {
    return ''
  }
  return code.replace(/\s+/g, '')
}

export function tnvedReviewsHaveCodeChanges(
  reviews: ApplicationTnvedReview[] = [],
): boolean {
  return reviews.some((entry) => (
    normalizeTnvedCode(entry.code) !== normalizeTnvedCode(entry.original_code)
  ))
}

function summaryProductMatchesInvoiceProduct(
  summaryProduct: OrderSummaryProduct,
  invoiceProduct: ProductParamsOutput,
): boolean {
  if (summaryProduct.name && invoiceProduct.name && summaryProduct.name === invoiceProduct.name) {
    return true
  }
  const summaryCode = normalizeTnvedCode(summaryProduct.code)
  const invoiceCode = normalizeTnvedCode(invoiceProduct.code)
  if (summaryCode && invoiceCode && summaryCode === invoiceCode) {
    return true
  }
  return false
}

function invoiceProductForSummaryRow(
  invoiceId: number,
  product: OrderSummaryProduct,
  invoiceProductsById?: Map<number, ProductParamsOutput[]>,
): ProductParamsOutput | undefined {
  const invoiceProducts = invoiceProductsById?.get(invoiceId)
  if (!invoiceProducts?.length) {
    return undefined
  }
  const customsIndex = product.customs_index
  if (customsIndex > 0) {
    const byIndex = invoiceProducts[customsIndex - 1]
    if (byIndex && summaryProductMatchesInvoiceProduct(product, byIndex)) {
      return byIndex
    }
  }
  return invoiceProducts.find((item) => summaryProductMatchesInvoiceProduct(product, item))
}

function findTnvedReviewForSummaryProduct({
  invoiceId,
  product,
  reviews,
  invoiceProductsById,
}: Pick<
  ApplicationEffectiveTnvedCodeArgs,
  'invoiceId' | 'product' | 'reviews' | 'invoiceProductsById'
>): ApplicationTnvedReview | undefined {
  if (!reviews?.length) {
    return undefined
  }

  const invoiceProduct = invoiceProductForSummaryRow(invoiceId, product, invoiceProductsById)
  if (invoiceProduct) {
    const byProductId = reviews.find((entry) => (
      entry.invoice_id === invoiceId && entry.product_id === invoiceProduct.id
    ))
    if (byProductId) {
      return byProductId
    }
  }

  const summaryCode = normalizeTnvedCode(product.code)
  return reviews.find((entry) => {
    if (entry.invoice_id !== invoiceId) {
      return false
    }
    const original = normalizeTnvedCode(entry.original_code)
    const revised = normalizeTnvedCode(entry.code)
    const appliedFrom = normalizeTnvedCode(entry.applied_from_code)
    if (summaryCode && (summaryCode === original || summaryCode === revised || summaryCode === appliedFrom)) {
      return true
    }
    if (entry.product_name && product.name && entry.product_name === product.name) {
      return true
    }
    return false
  })
}

function tnvedCodeBeforeChange(review: ApplicationTnvedReview): string {
  const appliedFrom = review.applied_from_code?.trim()
  if (appliedFrom) {
    return appliedFrom
  }
  return review.original_code?.trim() || ''
}

export function buildTnvedReviewDrafts(
  products: ProductParamsOutput[],
  saved: ApplicationTnvedReview[] = [],
): ApplicationTnvedReview[] {
  return products.map((product) => {
    const existing = saved.find(
      (entry) => entry.product_id === product.id && entry.invoice_id === product.invoice_id,
    )
    if (existing) {
      return { ...existing }
    }
    return {
      product_id: product.id,
      invoice_id: product.invoice_id,
      product_name: product.name || '',
      original_code: product.code || '',
      code: product.code || '',
      change_reason: '',
    }
  })
}

export function effectiveTnvedCodeForSummaryProduct({
  invoiceId,
  product,
  reviews,
  tnvedChangesConfirmed = false,
  invoiceProductsById,
}: ApplicationEffectiveTnvedCodeArgs): string {
  const calculationCode = product.code || ''
  const match = findTnvedReviewForSummaryProduct({
    invoiceId,
    product,
    reviews,
    invoiceProductsById,
  })
  if (!match) {
    return calculationCode
  }
  const revisedCode = match.code?.trim() || ''
  const originalCode = tnvedCodeBeforeChange(match) || calculationCode
  const hasChange = normalizeTnvedCode(revisedCode) !== normalizeTnvedCode(originalCode)
  if (hasChange && !tnvedChangesConfirmed) {
    return originalCode
  }
  return revisedCode || calculationCode
}

export function summaryProductTnvedReceipt({
  invoiceId,
  product,
  reviews,
  tnvedChangesConfirmed = false,
  invoiceProductsById,
  calculationSnapshotProductCodes,
}: ApplicationEffectiveTnvedCodeArgs): { displayCode: string; originalCode: string | null } {
  const displayCode = effectiveTnvedCodeForSummaryProduct({
    invoiceId,
    product,
    reviews,
    tnvedChangesConfirmed,
    invoiceProductsById,
  })
  const match = findTnvedReviewForSummaryProduct({
    invoiceId,
    product,
    reviews,
    invoiceProductsById,
  })
  if (!match) {
    return { displayCode, originalCode: null }
  }

  const beforeCode = tnvedCodeBeforeChange(match)
  const beforeNorm = normalizeTnvedCode(beforeCode)
  const afterNorm = normalizeTnvedCode(displayCode)
  const revisedNorm = normalizeTnvedCode(match.code)

  if (!beforeNorm || !afterNorm || beforeNorm === afterNorm) {
    if (beforeNorm && revisedNorm && beforeNorm !== revisedNorm && tnvedChangesConfirmed) {
      return { displayCode, originalCode: beforeCode }
    }
    const invoiceProduct = invoiceProductForSummaryRow(invoiceId, product, invoiceProductsById)
    const snapshotRaw = invoiceProduct && calculationSnapshotProductCodes
      ? calculationSnapshotProductCodes[String(invoiceProduct.id)]
      : undefined
    const snapshotNorm = normalizeTnvedCode(snapshotRaw)
    if (snapshotNorm && afterNorm && snapshotNorm !== afterNorm) {
      return { displayCode, originalCode: snapshotRaw || snapshotNorm }
    }
    return { displayCode, originalCode: null }
  }
  return { displayCode, originalCode: beforeCode }
}

export function useApplicationOrderProducts(orderId: string | undefined) {
  const dispatch = useAppDispatch()
  const skip = !orderId
  const { data: invoices, isLoading: invoicesLoading } = useInvoicesByOrderIdQuery(orderId!, { skip })
  const [ products, setProducts ] = useState<ProductParamsOutput[]>([])
  const [ productsLoading, setProductsLoading ] = useState(false)

  useEffect(() => {
    if (!invoices?.length) {
      setProducts([])
      return undefined
    }

    let cancelled = false
    setProductsLoading(true)
    Promise.all(
      invoices.map((invoice) => (
        dispatch(calcApi.endpoints.productsByInvoiceId.initiate(invoice.id))
          .unwrap()
          .catch(() => [] as ProductParamsOutput[])
      )),
    ).then((lists) => {
      if (!cancelled) {
        setProducts(lists.flat())
      }
    }).finally(() => {
      if (!cancelled) {
        setProductsLoading(false)
      }
    })

    return () => {
      cancelled = true
    }
  }, [ dispatch, invoices ])

  return {
    products,
    loading: invoicesLoading || productsLoading,
  }
}
